// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useState } from 'react';

import { Box, Collapse, Flex, Stack } from '@mantine/core';
import { useLocation } from 'react-router';

import { moduleRoute, type NavOption } from 'app/Layout/NavBar/globalNavigationMenu';
import { NavItem } from 'app/Layout/NavBar/NavBarContent/NavItem';
import { NavPopover } from 'app/Layout/NavBar/NavBarContent/NavPopover';
import { NavSectionHeader } from 'app/Layout/NavBar/NavBarContent/NavSectionHeader';
import styledNavLinkClasses from 'app/Layout/NavBar/NavBarContent/StyledNavLink.module.css';
import { navbarAnimationDuration, navbarAnimationTimingFunction } from 'app/Layout/NavBar/useAppShell';
import type { EvolveIconName } from 'assets/icons/EvolveIcon';
import { uuIDValidator } from 'helpers/uuIDValidator';

export const NavBarSection = ({
  navData,
  navbarExpanded,
  popoverOpened,
  onPopoverChanged,
}: {
  navData: {
    label: string;
    icon: EvolveIconName;
    options: NavOption[];
  };
  navbarExpanded: boolean;
  popoverOpened: boolean;
  onPopoverChanged: (popoverOpened: boolean) => void;
}) => {
  const location = useLocation();

  const [navOptionsOpened, setNavOptionsOpened] = useState(true);

  const routeIsActive = useCallback(
    (routePath: string) => {
      const pathNames = location.pathname.split('/').filter((x) => x);
      const pathNameWithoutUUID = pathNames.filter((x) => !uuIDValidator(x)).join('/');
      return pathNameWithoutUUID === `${moduleRoute}${routePath}`;
    },
    [location.pathname],
  );

  return (
    <Box key={navData.label} mb="xs">
      <Flex align="center">
        <NavPopover
          opened={popoverOpened}
          onChange={onPopoverChanged}
          position="right-start"
          disabled={navbarExpanded}
          label={navData.label}
          target={
            <NavSectionHeader
              navData={navData}
              navbarExpanded={navbarExpanded}
              navOptionsOpened={navOptionsOpened}
              onClick={() => {
                if (navbarExpanded) {
                  setNavOptionsOpened((s) => !s);
                }
              }}
            />
          }
        >
          {navData.options.map((opt) => (
            <NavItem
              key={`${navData.label}-${opt.label}`}
              item={opt}
              onClick={() => onPopoverChanged(false)}
              active={routeIsActive(opt.route)}
              withLabel
            />
          ))}
        </NavPopover>
      </Flex>
      <Collapse
        expanded={navbarExpanded && navOptionsOpened}
        transitionDuration={navbarAnimationDuration}
        transitionTimingFunction={navbarAnimationTimingFunction}
      >
        <Stack gap={0} flex={1} pt={6} pl={18}>
          {navData.options.map((opt) => {
            const isActive = routeIsActive(opt.route);
            return (
              <Box
                key={`${navData.label}-${opt.label}`}
                className={
                  isActive
                    ? [styledNavLinkClasses.navStackItem, styledNavLinkClasses.activeItem].join(' ')
                    : styledNavLinkClasses.navStackItem
                }
              >
                <NavItem item={opt} withLabel={navbarExpanded} active={isActive} />
              </Box>
            );
          })}
        </Stack>
      </Collapse>
    </Box>
  );
};
